import { Vector2 } from 'three';
import { radToDeg } from '@/util/math';
import { horizontalUnit2, verticalUnit2 } from '@/domain/geometry/vector2';

// ---------------------------------------------------------------------------
// COORDINATE-FRAME CONTRACT
//
// Every function here assumes landmarks in IMAGE coordinates: x increases to
// the right, y increases DOWNWARD (top-left origin). This matches the stored
// hip-spine API DTO and the clinical reference, and it is the frame in which
// the signed results below are defined.
//
// `lumbarLordosis` and `pelvicFemoralAngle` derive their sign purely from
// landmark-to-landmark vectors, so they are frame-independent (a y-flip leaves
// the sign unchanged). `sacralSlope` and `pelvicIncidence` describe a tilt
// relative to gravity, so their sign depends on knowing which way is "up" —
// they require this y-down frame. A caller working in a y-up frame (e.g. the
// post-flip pixel overlay map) must convert to y-down before calling.
// ---------------------------------------------------------------------------

/**
 * Acute angle in degrees between two 2D direction vectors, treating each as an
 * undirected line. Always in [0, 90].
 */
function acuteAngleBetween(u: Vector2, v: Vector2): number {
    return radToDeg(Math.atan2(Math.abs(u.cross(v)), Math.abs(u.dot(v))));
}

/**
 * Unsigned angle in degrees between two directed vectors. Always in [0, 180]
 * (the cross magnitude removes chirality; the signed dot keeps obtuse vs acute).
 */
function angleBetween(u: Vector2, v: Vector2): number {
    return radToDeg(Math.atan2(Math.abs(u.cross(v)), u.dot(v)));
}

/**
 * True when the sacral endplate "points posteriorly": its anterior endpoint is
 * more cranial (smaller y, in the y-down frame) than its posterior endpoint.
 * Drives the negative sign of both Sacral Slope and Pelvic Incidence — a
 * normally forward-inclined sacrum has its anterior edge lower (larger y) and
 * so reads positive.
 */
function sacrumPointsPosteriorly(sacralEndplate: { a: Vector2; b: Vector2 }): boolean {
    return sacralEndplate.a.y < sacralEndplate.b.y;
}

/**
 * Pelvic Incidence in degrees: the average of the angles measured at hjc1 and
 * hjc2 against the perpendicular to the sacral endplate, taken from the sacral
 * midpoint.
 *
 * Signed: negative when the sacrum points posteriorly (see
 * {@link sacrumPointsPosteriorly}), positive otherwise. `a` is the anterior
 * sacral endpoint, `b` the posterior.
 */
export function pelvicIncidence(
    sacralEndplate: { a: Vector2; b: Vector2 },
    hjc1: Vector2,
    hjc2: Vector2,
): number {
    const sacralDirection = new Vector2().subVectors(sacralEndplate.b, sacralEndplate.a);
    // Perpendicular to the sacral endplate. Direction does not matter for the
    // magnitude because each per-HJC angle is measured as an undirected line.
    const sacralPerpendicular = new Vector2(sacralDirection.y, -sacralDirection.x);
    const sacralMid = new Vector2().lerpVectors(sacralEndplate.a, sacralEndplate.b, 0.5);
    const pelvicIncidenceAtHjc1 = acuteAngleBetween(
        sacralPerpendicular,
        new Vector2().subVectors(hjc1, sacralMid),
    );
    const pelvicIncidenceAtHjc2 = acuteAngleBetween(
        sacralPerpendicular,
        new Vector2().subVectors(hjc2, sacralMid),
    );
    const magnitude = (pelvicIncidenceAtHjc1 + pelvicIncidenceAtHjc2) / 2;
    return sacrumPointsPosteriorly(sacralEndplate) ? -magnitude : magnitude;
}

/**
 * Lumbar Lordosis in degrees: the angle between the lumbar endplate and the
 * sacral endplate.
 *
 * Signed: positive when the two endplates open toward the anterior side
 * (normal lordosis), negative when they open posteriorly (kyphotic / flatback).
 * The sign is anchored to the anatomy — anterior is taken from the endplate
 * orientation (anterior endpoint `a` minus posterior endpoint `b`) and cranial
 * from the sacrum→lumbar midpoint vector — so it is independent of the image
 * y-convention.
 */
export function lumbarLordosis(
    sacralEndplate: { a: Vector2; b: Vector2 },
    lumbarEndplate: { a: Vector2; b: Vector2 },
): number {
    const sacralAnterior = new Vector2().subVectors(sacralEndplate.a, sacralEndplate.b);
    const lumbarAnterior = new Vector2().subVectors(lumbarEndplate.a, lumbarEndplate.b);
    const magnitude = acuteAngleBetween(sacralAnterior, lumbarAnterior);

    const sacralMid = new Vector2().lerpVectors(sacralEndplate.a, sacralEndplate.b, 0.5);
    const lumbarMid = new Vector2().lerpVectors(lumbarEndplate.a, lumbarEndplate.b, 0.5);
    const cranialDirection = new Vector2().subVectors(lumbarMid, sacralMid);

    // The endplates open anteriorly when the rotation from the sacral to the
    // lumbar endplate turns the same way as the cranial direction lies from the
    // sacral endplate. Comparing the two cross products is frame-independent:
    // a y-flip negates both, leaving their equality intact.
    const opensAnterior =
        Math.sign(sacralAnterior.cross(lumbarAnterior)) ===
        Math.sign(sacralAnterior.cross(cranialDirection));
    return opensAnterior ? magnitude : -magnitude;
}

/**
 * Sacral Slope in degrees: the angle between the sacral endplate and the
 * horizontal x-axis.
 *
 * Signed: negative when the sacrum points posteriorly (anterior edge cranial of
 * the posterior edge; see {@link sacrumPointsPosteriorly}), positive otherwise.
 * `a` is the anterior sacral endpoint, `b` the posterior.
 */
export function sacralSlope(sacralEndplate: { a: Vector2; b: Vector2 }): number {
    const magnitude = acuteAngleBetween(
        new Vector2().subVectors(sacralEndplate.b, sacralEndplate.a),
        horizontalUnit2(),
    );
    return sacrumPointsPosteriorly(sacralEndplate) ? -magnitude : magnitude;
}

/**
 * Pelvic-Femoral Angle in degrees at the HJC vertex, between rays
 * HJC→sacrum-midpoint and HJC→femoral-cortex.
 *
 * The angle is measured on the side that faces anterior — not unconditionally
 * the larger (reflex) of the two arcs. We compute the undirected interior angle
 * (0–180) and return it when its bisector points anterior, otherwise the reflex
 * complement (360 − interior). Anterior is taken from the sacral endplate
 * (anterior endpoint `a` minus posterior `b`), so the result depends only on
 * intrinsic geometry and is independent of the image y-convention.
 */
export function pelvicFemoralAngle(
    sacralEndplate: { a: Vector2; b: Vector2 },
    hjc: Vector2,
    femurCortex: Vector2,
): number {
    const sacralMid = new Vector2().lerpVectors(sacralEndplate.a, sacralEndplate.b, 0.5);
    const toSacrum = new Vector2().subVectors(sacralMid, hjc);
    const toCortex = new Vector2().subVectors(femurCortex, hjc);
    const interior = angleBetween(toSacrum, toCortex);

    const anteriorDirection = new Vector2().subVectors(sacralEndplate.a, sacralEndplate.b);
    const bisector = toSacrum.clone().normalize().add(toCortex.clone().normalize());
    const facesAnterior = bisector.dot(anteriorDirection) > 0;
    return facesAnterior ? interior : 360 - interior;
}

/**
 * Anterior Pelvic Plane (APP) tilt in degrees. Magnitude is the acute angle
 * (0–90°) between the pubic-symphysis → ASIS-midpoint line and the y-axis.
 *
 * Sign convention: positive when the APP ray (pubic symphysis → ASIS midpoint)
 * leans to the *anterior* side of the vertical reference ray — anterior pelvic
 * tilt; negative when it leans posterior (posterior pelvic tilt / PPT). This
 * matches the clinical convention and the HipCheck reference.
 *
 * "Anterior" is purely the horizontal sense of the sacral endplate: the anterior
 * endpoint lies toward the patient's front, so `sign(sacrumAnterior.x −
 * sacrumPosterior.x)` tells us which way (±x) the patient faces. Only that
 * horizontal sense matters relative to the vertical ray — the sacrum's vertical
 * (sacral-slope) component must NOT enter the sign, or a steeply tilted sacrum
 * would corrupt it. The magnitude is the acute angle to vertical. Both the sign
 * (x only) and the magnitude (acute) are therefore independent of the y-axis
 * convention (y-up or y-down) the caller uses.
 *
 * The ASIS line is treated as undirected; the sacral anterior/posterior pair
 * is directed because the sign of the result depends on it.
 */
export function appTilt(
    asisLine: { a: Vector2; b: Vector2 },
    pubicSymphysis: Vector2,
    sacrumAnterior: Vector2,
    sacrumPosterior: Vector2,
): number {
    const asisMid = new Vector2().lerpVectors(asisLine.a, asisLine.b, 0.5);
    // The APP ray runs from the vertex (pubic symphysis) up to the ASIS midpoint.
    const appRay = new Vector2().subVectors(asisMid, pubicSymphysis);
    const magnitude = acuteAngleBetween(appRay, verticalUnit2());

    // Positive when the ray leans to the anterior side of vertical. Anterior is
    // the horizontal direction of the sacral endplate's anterior endpoint.
    const anteriorIsPositiveX = sacrumAnterior.x >= sacrumPosterior.x;
    const rayLeansAnterior = anteriorIsPositiveX ? appRay.x > 0 : appRay.x < 0;
    return rayLeansAnterior ? magnitude : -magnitude;
}
